import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Minus } from '@/assets/icons/Minus'
import { Plus } from '@/assets/icons/Plus'
import { useAppDispatch, useAppSelector } from '@/hooks/store'
import { Service, TimeSchedule } from '@/models/service'
import { timeScheduleSlice } from '@/store/timeScheduleSlice'
import { formatPrice } from '@/utils/price'
import { adjustEndTime, getTimeScheduleKey } from '@/utils/timeSchedules'
import { IconButton } from '../IconButton'

interface Props {
  availability: {
    global: number
    group: number
    timeSchedule: number
  }
  service: Service
  timeSchedule: TimeSchedule
}

export function TimeScheduleQuantityPicker(props: Props) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const addedServices = useAppSelector(
    timeScheduleSlice.selectors.addedServices
  )
  const key = getTimeScheduleKey(props.timeSchedule)
  const localQuantity = addedServices[key]?.bought ?? 0

  const label = useMemo(() => {
    if (props.service.scheduleType === 'daily') {
      return t('timeSchedulesDialog.quantity')
    }

    const time = `${props.timeSchedule.startTime} - ${adjustEndTime(props.timeSchedule.endTime)}`
    return props.timeSchedule.name
      ? `${props.timeSchedule.name} · ${time}`
      : time
  }, [props.service.scheduleType, props.timeSchedule])

  const isRemoveDisabled = localQuantity < 1

  const global = props.availability.global
  const group = props.availability.group
  const timeSchedule = props.availability.timeSchedule

  const addedService = addedServices[getTimeScheduleKey(props.timeSchedule)]
  const qty = addedService?.bought ?? 0

  const availableQuantity = props.service.hasLimit
    ? Math.min(global, group, timeSchedule - qty)
    : Infinity

  const isAddDisabled = availableQuantity < 1

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="font-bold">{label}</div>
        <div className="text-sm text-secondary">
          {formatPrice(props.timeSchedule.price)}
        </div>
      </div>

      <div className="flex shrink-0">
        <IconButton
          ariaLabel={t('itemQuantityController.remove')}
          icon={Minus}
          isDisabled={isRemoveDisabled}
          onClick={() => {
            dispatch(
              timeScheduleSlice.actions.changeTimeScheduleAmount({
                amount: localQuantity - 1,
                timeSchedule: props.timeSchedule,
              })
            )
          }}
        />

        <div className="flex w-10 items-center justify-center px-0.5 font-medium">
          {localQuantity}
        </div>

        <IconButton
          ariaLabel={t('itemQuantityController.add')}
          icon={Plus}
          isDisabled={isAddDisabled}
          onClick={() => {
            dispatch(
              timeScheduleSlice.actions.changeTimeScheduleAmount({
                amount: localQuantity + 1,
                timeSchedule: props.timeSchedule,
              })
            )
          }}
        />
      </div>
    </div>
  )
}
